import { produce } from 'immer';
import { getTimeString } from '../components/AppUtils';
import {
  createInitialDeviceData,
  prepareForNewRead,
  resetChanges,
  commitWrite,
} from '../models/deviceData';
import { loadDemoMode } from '../models/demoMode';

// Build a cleared device data state ("no device scanned yet"). Reuses the
// initial-data factory but keeps serialNumber null so the UI shows the
// "no device" hint instead of an all-zero serial. Optionally seeds the uri.
function buildResetDeviceData (uri) {
  const deviceData = createInitialDeviceData();
  deviceData.serialNumber = null;
  if (uri) deviceData.uri = uri;
  return deviceData;
}

function writeInitializerType (state) {
  if (state.settingForceWrite || state.settingSeamlessWrite) {
    return "warning"
  } else {
    return "info"
  }
}

function writeInitializerHeadline (state) {
  if (state.settingForceWrite || state.settingSeamlessWrite) {
    return "Warning"
  } else {
    return "Information"
  }
}

function writeInitializerSubHeadline (state) {
  if (state.settingForceWrite) {
    return "Froce write mode enabled"
  } else if (state.settingSeamlessWrite) {
    return "Seamless write mode enabled"
  } else {
    return  "Write mode enabled"
  }
}

function writeInitializerText (state, value) {
  if (state.settingForceWrite) {
    return "Data is written to every device without checking."
  } else if (state.settingSeamlessWrite) {
    return "Writing to other devices of the same type allowed."
  } else {
    return (value <= 0)? "Data will be written to scanned device.":`${value} change(s) will be written to device.`
  }
}

function vibrate(pattern) {
  if (navigator.vibrate) {
    navigator.vibrate(pattern);
  }
}

export const reducer = (state, action) => {
  let timeString = getTimeString();
  console.debug("Dispatch:", action.type, action.value ? action.value : "");
  switch (action.type) {
    case 'ndef/control/idle':
      return {
        ...state,
        ndefControl: 'idle',
      };
    case 'ndef/control/reading':
      return {
        ...state,
        ndefControl: 'reading',
      };
    case 'ndef/control/writing':
      // Validation, if writing is possible
      if (state.nfcDataValidationError) {
        vibrate([200, 30, 200, 30, 200]);
        return { ...state,
          scanDialog_alertType:"error",
          scanDialog_alertHeadline:`Error S06`,
          scanDialog_alertSubHeadline: "Parameter validation failed",
          scanDialog_alertText: "Writing not possible. Please check and correct the form!",
          scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
          scanDialog_ImageCardHeadline:"Scan disabled",
          scanDialog_ImageCardSubtext:"Please check the current changes for validity.",
          scanDialog_showModeChanger:false,
          scanDialog_dialogBtnText:"Cancel"
        };
      }
      return {
        ...state,
        ndefControl: 'writing',
      };
    case 'ndef/control/updateScanTime':
      return produce(state, (draft) => {
        draft.deviceData.scanTime = timeString;
      });
    case 'ndef/control/setForNewRead':
      return {
        ...state,
        deviceData: prepareForNewRead(state.deviceData),
        nfcAvailable: true,
      };
    case 'ndef/control/resetChanges':
      return {
        ...state,
        deviceData: resetChanges(state.deviceData),
      };
    case 'ndef/scan/write/done':
      // A write always implies a successful scan, so this is a complete
      // transition: it includes the ndef/scan/done effects (no need to dispatch
      // ndef/scan/done separately before it).
      return produce(state, (draft) => {
        draft.uiNdefState = 'done';
        draft.deviceData = commitWrite(state.deviceData);
        draft.deviceData.scanTime = timeString;
      });
    case 'ndef/scan/scanning':
      return {
        ...state,
        uiNdefState: 'scanning',
      };
    case 'ndef/scan/processing':
      return {
        ...state,
        uiNdefState: 'processing',
      };
    case 'ndef/scan/failed':
      return {
        ...state,
        uiNdefState: 'error',
      };
    case 'ndef/scan/done':
      return produce(state, (draft) => {
        draft.uiNdefState = 'done';
        draft.deviceData.scanTime = timeString;
      });
    case 'nfc/status/available':
      return {
        ...state,
        nfcAvailable: action.value,
      };
    case 'nfc/status/permission':
      return {
        ...state,
        nfcPermission: action.value,
      };
    case 'nfc/status/notAvailable':
      return {
        ...state,
        nfcAvailable: false,
      };
    case 'data/nfcTagSnUpdated':
      return produce(state, (draft) => {
        draft.deviceData.serialNumber = action.value;
        draft.deviceData.source = 'scan';
      });
    case 'app/originUrlUpdated':
      return { ...state, currentURL: action.value };
    case 'data/V/updated':
      return produce(state, (draft) => {
        draft.deviceData.data.V = action.value;
      });
    case 'data/VP/updated':
      return produce(state, (draft) => {
        draft.deviceData.data.VP = action.value;
      });
    case 'data/P/readOut':
      return produce(state, (draft) => {
        draft.deviceData.data.P = action.value;
        draft.deviceData.origin.P = action.value;
      });
    case 'data/PR/readOut':
      return produce(state, (draft) => {
        draft.deviceData.data.PR = action.value;
        draft.deviceData.origin.PR = action.value;
      });
    case 'data/PP/readOut':
      return produce(state, (draft) => {
        draft.deviceData.data.PP = action.value;
        draft.deviceData.origin.PP = action.value;
      });
    case 'data/P/updated':
      return produce(state, (draft) => {
        draft.deviceData.data.P = action.value;
      });
    case 'data/PR/updated':
      return produce(state, (draft) => {
        draft.deviceData.data.PR = action.value;
      });
    case 'data/PP/updated':
      return produce(state, (draft) => {
        draft.deviceData.data.PP = action.value;
      });
    case 'data/CP/updated':
      return produce(state, (draft) => {
        draft.deviceData.data.CP = action.value;
      });
    case 'data/V/imported':
      return produce(state, (draft) => {
        draft.deviceData.data.V = action.value;
        draft.deviceData.source = 'import';
      });
    case 'data/VP/imported':
      return produce(state, (draft) => {
        draft.deviceData.data.VP = action.value;
        draft.deviceData.source = 'import';
      });
    case 'data/P/imported':
      return produce(state, (draft) => {
        draft.deviceData.data.P = action.value;
        draft.deviceData.origin.P = action.value;
        draft.deviceData.source = 'import';
      });
    case 'data/PR/imported':
      return produce(state, (draft) => {
        draft.deviceData.data.PR = action.value;
        draft.deviceData.origin.PR = action.value;
        draft.deviceData.source = 'import';
      });
    case 'data/PP/imported':
      return produce(state, (draft) => {
        draft.deviceData.data.PP = action.value;
        draft.deviceData.origin.PP = action.value;
        draft.deviceData.source = 'import';
      });
    case 'data/CP/imported':
      return produce(state, (draft) => {
        draft.deviceData.data.CP = action.value;
        draft.deviceData.source = 'import';
      });
    case 'data/checkTag/setStatus':
      return {
        ...state,
        checkTag: action.value
      };
    case 'data/checkTag/done':
      if (action.value.status === 'tagOkay' && action.value.message === 'checkDataImported') {
        return produce(state, (draft) => {
          draft.deviceData.uri = action.value.nfcUri;
          draft.deviceData.schemaCompressed = action.value.nfcSchemaCompressed;
          draft.deviceData.data.CP = action.value.nfcDataCP;
          draft.deviceData.data.P = action.value.nfcDataP;
          draft.deviceData.origin.P = action.value.nfcDataP;
          draft.deviceData.data.PP = action.value.nfcDataPP;
          draft.deviceData.origin.PP = action.value.nfcDataPP;
          draft.deviceData.data.PR = action.value.nfcDataPR;
          draft.deviceData.origin.PR = action.value.nfcDataPR;
          draft.deviceData.data.V = action.value.nfcDataV;
          draft.deviceData.data.VP = action.value.nfcDataVP;
          draft.checkTagResult = action.value;
          draft.checkTag = 'done';
        });
      }
      return produce(state, (draft) => {
        draft.deviceData.uri = action.value.nfcUri;
        draft.deviceData.schemaCompressed = action.value.nfcSchemaCompressed;
        draft.checkTagResult = action.value;
        draft.checkTag = 'done';
      });
    case 'data/urlUpdated':
      return produce(state, (draft) => {
        draft.deviceData.uri = action.value;
      });
    case 'data/themeUriUpdated':
      return { ...state, themeUri: action.value };
    case 'data/setValidError':
      return { ...state, nfcDataValidationError: action.value };
    case 'data/S/updated':
      return produce(state, (draft) => {
        draft.deviceData.schema = action.value;
      });
    case 'data/S/updatedCompressed':
      return produce(state, (draft) => {
        draft.deviceData.schemaCompressed = action.value;
      });
    case 'data/source/set':
      return produce(state, (draft) => {
        draft.deviceData.source = action.value;
      });
    // Load a complete IDeviceData at once (e.g. demo / default value bundles).
    case 'data/device/loaded':
      return produce(state, (draft) => {
        draft.deviceData = action.value;
      });
    // Clear device data back to "no device" (optional uri in action.value).
    case 'data/device/reset':
      return produce(state, (draft) => {
        draft.deviceData = buildResetDeviceData(action.value);
      });
    // ## Application User Interface
    case 'app/ui/card/nfcNotAvailable':
      return { ...state, uiCardNfcNotAvailable: action.value };
    case 'app/ui/card/nfcNotGranted':
      return { ...state, uiCardNfcNotGranted: action.value };
    case 'app/ui/card/appInstallable':
      return { ...state, uiCardAppInstallable: action.value };
    case 'app/ui/card/appOpen':
      return { ...state, uiCardAppOpen: action.value };
    case 'app/ui/dialog/ndef/show':
      return { ...state, uiScanDialog: action.value };
    case 'app/ui/dialog/default/show':
      return { ...state, uiDialog: action.value };
    case 'app/ui/dialog/default/close':
      return { ...state, uiDialog: null };
    case 'app/ui/menu/dialog/default/close':
      return { ...state, uiMenuDialog: null };
    case 'app/ui/menu/dialog/goto':
      return { ...state, uiMenuDialog: action.value };
    case 'app/ui/dataPage/tab':
      return { ...state, uiDataPageTab: action.value };
    case 'app/ui/control/nfcGrantCheck':
      return {
        ...state,
        nfcGrantCheck: action.value,
      };
    case 'app/ui/control/appInstallTrigger':
      return {
        ...state,
        appInstallPromptTrigger: action.value,
      };
    case 'app/ui/dialog/error':
      return {
        ...state,
        errorTitle: action.title,
        errorText: action.text
      };
    case 'app/ui/snackbar/show':
      return { ...state,
        snackbarOpen: true,
        snackbarMessage: action.message,
        snackbarSeverity: action.severity
      };
    case 'app/ui/snackbar/close':
      return { ...state,
        snackbarOpen: false,
        snackbarMessage: null
      };
    // ### Scan Dialogue Reducers
    case 'app/ui/scanDialog/readInitializer':
      return { ...state,
        scanDialog_alertType:(action.value.changedParams === 0)?"info":"warning",
        scanDialog_alertHeadline: "Information",
        scanDialog_alertSubHeadline: "Read mode enabled",
        scanDialog_alertText: (action.value.scanTime!=="unknown" || action.value.changedParams > 0)?`Current data ${(action.value.changedParams>0)?('with '+action.value.changedParams+' change(s)'):''} will be discarded.`:"Read data from a device first.",
        scanDialog_ImageCardType:"start", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Ready to scan",
        scanDialog_ImageCardSubtext:"To scan, hold your phone close to the device.",
        scanDialog_showModeChanger:(action.value.scanTime==="unknown")?false:true,
        scanDialog_dialogBtnText:"Cancel"
      };
    case 'app/ui/scanDialog/writeInitializer':
      return { ...state,
        scanDialog_alertType: writeInitializerType(state),
        scanDialog_alertHeadline: writeInitializerHeadline(state),
        scanDialog_alertSubHeadline: writeInitializerSubHeadline(state),
        scanDialog_alertText: writeInitializerText(state, action.value),
        // scanDialog_alertText:(action.value <= 0)? "Data will be written to scanned device.":`${action.value} change(s) will be written to device.`,
        scanDialog_ImageCardType:"start", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Ready to scan",
        scanDialog_ImageCardSubtext:"To scan, hold your phone close to the device.",
        scanDialog_showModeChanger:true,
        scanDialog_dialogBtnText:"Cancel"
      };
    case 'app/ui/scanDialog/idleInitializer':
      return { ...state,
        scanDialog_alertType:(action.value.changedParams === 0)?"info":"warning",
        scanDialog_alertHeadline: "Information",
        scanDialog_alertSubHeadline: "Choose read or write mode",
        scanDialog_alertText: "Reading will discard the current data.",
        scanDialog_ImageCardType:"start", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Choose mode",
        scanDialog_ImageCardSubtext:"To scan, hold your phone close to the device.",
        scanDialog_showModeChanger:(action.value.scanTime==="unknown")?false:true,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Device was scanned with wrong brand value version.
    case 'app/ui/scanDialog/incompatibleDevice':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S01`,
        scanDialog_alertSubHeadline: "Device not compatible",
        scanDialog_alertText: "The device is not compatible with this app.",
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Scan failed",
        scanDialog_ImageCardSubtext:"Please scan a different device.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // RE00 - General read / check error
    // RE01 - Wrong record media type
    // RE02 - Record Key unknown
    // RE03 - Empty record, tag technology or format not supported (NdefFormatable)
    // RE04 - Record type not supported
    // RE05 - Record Data invalid
    // RE06 - Record Key invalid
    case 'app/ui/scanDialog/unknownDevice':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S02-${action.value}`,
        scanDialog_alertSubHeadline: `Unknown or faulty device detected`,
        scanDialog_alertText: "The device is either faulty or not compatible with this app.",
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Scan failed",
        scanDialog_ImageCardSubtext:"Please try again or scan another device.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Sn check failed while writing
    case 'app/ui/scanDialog/writingToSameDevice':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S03`,
        scanDialog_alertSubHeadline: "Other device recognised by serial number",
        scanDialog_alertText: `The first read is made via no. ${action.value.src.substring(0, 5)}, but the write attempt is made on no. ${action.value.dest.substring(0, 5)}.`,
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Write attempt blocked",
        scanDialog_ImageCardSubtext:"Read device at first before writing or activate semless writing in settings menu.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Device type (article code) check failed while writing
    case 'app/ui/scanDialog/writingToDiffDevice':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S04`,
        scanDialog_alertSubHeadline:"Different device detected",
        scanDialog_alertText: `No data can be written from device ${action.value.src} to device ${action.value.dest}.`,
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Write attempt failed",
        scanDialog_ImageCardSubtext:`Please try again with ${action.value.src} or read from device ${action.value.dest} first.`,
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Schema version is different
    case 'app/ui/scanDialog/writingMajorVersChanged':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S05`,
        scanDialog_alertSubHeadline: "Different configuration version detected",
        scanDialog_alertText: `Version ${action.value.srcVer} of the current configuration is not compatible with target version ${action.value.destVer}. `,
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Write attempt failed",
        scanDialog_ImageCardSubtext:"Read this device at first before writing or try another device.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Empty tag (no records found)
    case 'app/ui/scanDialog/emptyTag':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S07`,
        scanDialog_alertSubHeadline: "Empty tag found",
        scanDialog_alertText: "No data available. Please wait a moment and try again.",
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Scan failed",
        scanDialog_ImageCardSubtext:"Please try again or use another device.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Write error
    case 'app/ui/scanDialog/writeFailed':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S08`,
        scanDialog_alertSubHeadline: "Transmission error",
        scanDialog_alertText: "Writing cancelled due to incorrect or incomplete data. Maybe your device has been detached from the tag.",
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Write attempt failed",
        scanDialog_ImageCardSubtext:"Please try again and keep device intact during scan.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Json format error
    case 'app/ui/scanDialog/jsonParseError':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S09`,
        scanDialog_alertSubHeadline: "Incorrect data",
        scanDialog_alertText: "Incorrect data found. Please wait a moment and try again. ",
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Scan failed",
        scanDialog_ImageCardSubtext:"Please try again or use another device.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    // Default error
    case 'app/ui/scanDialog/unknownError':
      vibrate([200, 30, 200, 30, 200]);
      return { ...state,
        scanDialog_alertType:"error",
        scanDialog_alertHeadline:`Error S99-${action.value}`,
        scanDialog_alertSubHeadline: `Unknown error`,
        scanDialog_alertText: "An unknown scan error occurred.",
        scanDialog_ImageCardType:"error", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Scan failed",
        scanDialog_ImageCardSubtext:"Please try again or scan another device.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    case 'app/ui/scanDialog/processing':
      return { ...state,
        scanDialog_alertType:"processing",
        scanDialog_alertHeadline:"Information",
        scanDialog_alertSubHeadline: "Scan in progress",
        scanDialog_alertText: "Please wait ...",
        scanDialog_ImageCardType:"progress", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Transmitting data...",
        scanDialog_ImageCardSubtext:"Please hold still while transmitting.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Cancel"
      };
    case 'app/ui/scanDialog/done':
      vibrate([100, 30, 100]);
      return { ...state,
        themeUri: state.deviceData.uri,
        scanDialog_alertType:"success",
        scanDialog_alertHeadline:"Scan status",
        scanDialog_alertSubHeadline: "Successful",
        scanDialog_alertText: (!action.value || action.value<=0)?"Data has been updated.":`${action.value} parameter(s) has been updated on the device.`,
        scanDialog_ImageCardType:"success", //image["start", "progress", "success", "error"]
        scanDialog_ImageCardHeadline:"Scan complete",
        scanDialog_ImageCardSubtext:"Data has been successfully transmitted.",
        scanDialog_showModeChanger:false,
        scanDialog_dialogBtnText:"Done",
      };
    case 'app/ui/scanDialog/modeSwitch/available':
      return {
        ...state,
        scanDialogModeSwitch: true,
      };
    case 'app/ui/scanDialog/modeSwitch/unavailable':
      return {
        ...state,
        scanDialogModeSwitch: false,
      };

    case 'app/ui/importConfigName/set':
      return {
        ...state,
        uiImportConfigName: action.value
      };
    // ## Application User Settings
    case 'app/settings/userAuthorized':
      return {
        ...state,
        settingAuthenticated: action.value
      };
    case 'app/settings/seamlessRead/toggle':
      return {
        ...state,
        settingSeamlessRead: action.value
      };
    case 'app/settings/seamlessWrite/toggle':
      return {
        ...state,
        settingSeamlessWrite: action.value
      };
    case 'app/settings/forceWrite/toggle':
      return {
        ...state,
        settingForceWrite: action.value
      };
    case 'app/settings/demoMode/toggle':
      return produce(state, (draft) => {
        draft.settingDemoMode = action.value;
        // Entering oder leaving demo mode starts fresh: reset device data
        draft.deviceData = buildResetDeviceData();
      });
    // ## Application Status
    case 'app/status/online':
      return {
        ...state,
        appOnline: action.value
      };
    case 'app/status/installation/':
      return {
        ...state,
        appStatusInstallation: action.value
      };
    case 'app/status/displayMode':
      return {
        ...state,
        appStatusDisplayMode: action.value,
      };
    // ## Application Theme by Brands
    case 'app/brand/logoUrl/updated':
      return { ...state, appBrandLogoUrl: action.value };
    case 'app/brand/data/updated':
      return { ...state,
        brandName: action.brandName,
        brandTitle: action.brandTitle,
        brandShowLabel: action.brandShowLabel,
        brandColor1: action.brandColor1,
        brandColor2: action.brandColor2,
        brandOwner: action.brandOwner,
        brandStreet: action.brandStreet,
        brandZip: action.brandZip,
        brandCity: action.brandCity,
        brandMail: action.brandMail,
        brandPhone: action.brandPhone
      };
    case 'app/brand/manifest/updated':
      console.debug("Dispatch:", action.type, action.appName, action.appNameShort)
      return { ...state,
        appManifestName: action.appName,
        appManifestShortName: action.appNameShort
      };
    case 'app/env/set':
      console.debug("Dispatch:", action.type, "Value: ", action.value)
      return { ...state,
        appEnv: action.value
      };
    case 'app/env/resetToNoData':
      console.debug("Dispatch:", action.type, "Value: ", action.value)
      return produce(state, (draft) => {
        draft.uiNdefState = 'done';
        draft.deviceData.scanTime = 'unknown';
      });
    case 'app/sw/init':
      console.debug("Dispatch:", action.type, "Value: ", action.value)
      return {
        ...state,
        appSwInitialized: true,
      };
    case 'app/sw/update':
      console.debug("Dispatch:", action.type, "Reg: ", action.value)
      return {
        ...state,
        appSwUpdated: true,
        appSwRegistration: action.value,
      };
    default: return state;
  }
}

// Nested device data updates use 'immer' (produce) and the helpers in
// src/models/deviceData.ts. https://www.npmjs.com/package/immer

export const initialState = {
  brandName: '', //
  brandTitle: '', // Sub title
  brandShowLabelOnIOs: false,
  brandColor1: '#005d9d', // Primary Color
  brandColor2: '#00796b', // Secondary Color
  brandOwner: false, // Show Product Owner Info
  brandStreet: '',
  brandZip: '',
  brandCity: '',
  brandMail: '',
  brandPhone: '',
  appManifestName: 'NFC Configuration',
  appManifestShortName: 'NFC Config',
  appBrandLogoUrl: 'image/kde/logo.png',
  appOnline: true,
  currentURL: 'undefined',
  themeUri: null,
  // All data read from / to be written to a single compatible device.
  // See src/interfaces/IDeviceData.ts and src/models/deviceData.ts
  deviceData: createInitialDeviceData(),
  nfcDataValidationError: false,
  checkTag: 'pending', // pending, done
  checkTagResult: {},
  nfcAvailable: false,
  nfcPermission: 'prompt', // [granted, denied, prompt]
  nfcGrantCheck: false,
  appInstallPromptTrigger: false,
  appStatusDisplayMode: 'undefined', // [browser, pwa, undefined]
  appStatusInstallation: 'undefined', // [undefined, uninstalled, progress, installed] Keeps record on if app is installed or not
  uiCardNfcNotAvailable: 'show',
  uiCardNfcNotGranted: 'hide',
  uiCardAppInstallable: 'hide',
  uiCardAppOpen: 'hide',
  uiDialog: null, // [share, devmode, demomode, import]
  uiMenuDialog: null, // [settings, about, help]
  uiDataPageTab: 0, // 0 = values, 1 = parameters
  uiScanDialog: false, // Show scanDialog or not´
  uiImportConfigName: 'unknown',
  ndefControl: 'idle', // [idle, reading, writing]
  uiNdefState: 'done', // [done, scanning, error, ...]
  errorTitle: null,
  errorText: null,
  snackbarOpen: false,
  snackbarMessage: null,
  snackbarSeverity:'info', // success, info, warning, error
  settingAuthenticated: false,
  settingSeamlessRead: false, // [state to handle seamless read]
  settingSeamlessWrite: false, // [state to handle seamless write]
  settingForceWrite: false, // [state to handle seamless write]
  settingDemoMode: loadDemoMode(), // global demo status, persisted in localStorage
  appEnv:'prd',
  // Service Worker
  appSwInitialized: false,
  appSwUpdated: false,
  appSwRegistration: null,
  //Dialogue States
  scanDialog_alertType:"", //alert type ["error", "info", "success", "warning", "processing"]
  scanDialog_alertHeadline:"",
  scanDialog_alertSubHeadline:"",
  scanDialog_alertText:"",

  scanDialog_ImageCardType:"", //image["start", "progress", "success", "error"]
  scanDialog_ImageCardHeadline:"No headline loaded",
  scanDialog_ImageCardSubtext:"No subtext loaded",

  scanDialog_showModeChanger:true,
  scanDialog_dialogBtnText:"Cancel",
  scanDialogModeSwitch:false
};
